import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { blogPosts, blogCategories, type BlogPost } from '../../mocks/blog';
import FormHoneypot from '@/components/feature/FormHoneypot';
import TurnstileField, {
  type TurnstileFieldHandle,
} from '@/components/feature/TurnstileField';
import { isTurnstileConfigured } from '@/config/turnstileEnv';
import { honeypotFilled } from '@/lib/formSpamGuard';
import { submitEmailLead } from '@/services/submitToKailac';

export default function BlogPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('All');
  const [emailInput, setEmailInput] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [newsletterBusy, setNewsletterBusy] = useState(false);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileFieldHandle>(null);

  const filtered = activeCategory === 'All' ? blogPosts : blogPosts.filter((p) => p.category === activeCategory);
  const featured = blogPosts.filter((p) => p.featured);

  useEffect(() => {
    document.title = 'Business Funding Blog & Resources | Expert Guides | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Expert articles, funding guides, and practical resources for business owners and entrepreneurs. Learn about 0% interest funding, credit preparation, eligibility, and how to access business capital. English and Spanish content.');
  }, []);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNewsletterError(null);
    if (!emailInput) return;
    const fd = new FormData(e.currentTarget);
    if (honeypotFilled(fd)) {
      setSubscribed(true);
      return;
    }
    setNewsletterBusy(true);
    try {
      let captchaVerified = false;
      if (isTurnstileConfigured()) {
        const token = await turnstileRef.current?.ensureToken();
        if (!token) {
          setNewsletterError(t('form_captcha_failed'));
          return;
        }
        captchaVerified = true;
      }
      try {
        await submitEmailLead(emailInput, 'blog_newsletter', {
          recaptchaVerified: captchaVerified,
        });
      } catch (_err) {
        /* silent fail */
      }
      turnstileRef.current?.reset();
      setSubscribed(true);
    } finally {
      setNewsletterBusy(false);
    }
  };

  const leadItems = [
    'blog_lead_item_1',
    'blog_lead_item_2',
    'blog_lead_item_3',
    'blog_lead_item_4',
  ];

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#F8F3FB] pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('blog_eyebrow')}</p>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            {t('blog_hero_title_1')}<br />
            <span className="text-[#4F2D63]">{t('blog_hero_title_2')}</span>
          </h1>
          <p className="text-base text-gray-500 max-w-xl mx-auto">
            {t('blog_hero_subtitle')}
          </p>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">{t('blog_featured_title')}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
            {featured.map((post: BlogPost) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-2xl border border-[#E9D9F2] overflow-hidden hover:border-[#DBB6EE] hover:-translate-y-0.5 transition-all cursor-pointer block"
              >
                <div className="w-full h-44 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-[#F8F3FB] text-[#4F2D63] px-2.5 py-1 rounded-full font-medium">{post.category}</span>
                    <span className="text-xs text-gray-400">{post.readTime}</span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-[#4F2D63] transition-colors">{post.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{post.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat ? 'bg-[#4F2D63] text-white' : 'bg-gray-100 text-gray-600 hover:bg-[#F8F3FB]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* All Posts */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post: BlogPost) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-2xl border border-[#E9D9F2] overflow-hidden hover:border-[#DBB6EE] hover:-translate-y-0.5 transition-all cursor-pointer block"
              >
                <div className="w-full h-40 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-[#F8F3FB] text-[#4F2D63] px-2.5 py-1 rounded-full font-medium">{post.category}</span>
                    <span className="text-xs text-gray-400">{post.readTime}</span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2 leading-snug group-hover:text-[#4F2D63] transition-colors">{post.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{post.excerpt}</p>
                  <div className="flex items-center gap-1 mt-4 text-[#4F2D63] text-xs font-semibold">
                    {t('blog_read_more')} <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#E9D9F2] mx-auto mb-5">
            <i className="ri-mail-open-line text-[#4F2D63] text-xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('blog_newsletter_title')}</h2>
          <p className="text-base text-gray-500 mb-8">{t('blog_newsletter_subtitle')}</p>
          {subscribed ? (
            <div className="flex items-center justify-center gap-2 text-[#0F766E] font-semibold">
              <i className="ri-check-circle-line text-xl"></i> {t('blog_newsletter_success')}
            </div>
          ) : (
            <form
              id="blog-newsletter-form"
              onSubmit={handleSubscribe}
              className="relative flex flex-col gap-3 max-w-lg mx-auto"
            >
              <FormHoneypot />
              <TurnstileField ref={turnstileRef} />
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-3 rounded-xl border border-[#E9D9F2] text-sm focus:outline-none focus:border-[#4F2D63] min-w-0"
                />
                <button
                  type="submit"
                  disabled={newsletterBusy}
                  className="px-6 py-3 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 sm:self-start"
                >
                  {t('blog_newsletter_btn')}
                </button>
              </div>
              {newsletterError ? (
                <div className="text-center space-y-1">
                  <p className="text-sm text-red-600" role="alert">
                    {newsletterError}
                  </p>
                  {isTurnstileConfigured() ? (
                    <p className="text-xs text-gray-500">{t('form_captcha_hint')}</p>
                  ) : null}
                </div>
              ) : null}
            </form>
          )}
        </div>
      </section>

      {/* Lead Magnet: Funding Readiness Checklist */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-[#F8F3FB] border border-[#E9D9F2] rounded-2xl p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">{t('blog_lead_eyebrow')}</p>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('blog_lead_title')}</h2>
              <p className="text-gray-500 text-base leading-relaxed mb-6">
                {t('blog_lead_desc')}
              </p>
              <div className="space-y-2">
                {leadItems.map((key) => (
                  <div key={key} className="flex items-center gap-2">
                    <i className="ri-check-line text-[#4F2D63] text-sm"></i>
                    <span className="text-sm text-gray-600">{t(key)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[#E9D9F2]">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('blog_lead_form_title')}</h3>
              <Link
                to="/contact"
                className="block w-full py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm text-center transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('blog_lead_cta')}
              </Link>
              <p className="text-xs text-gray-400 text-center mt-3">{t('blog_lead_disclaimer')}</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
